import { useLayoutEffect, useRef, useState } from "react";

import { Button } from "#components/Button";
import { SearchField } from "#components/SearchField";
import { SearchIcon } from "#icons";
import { cn } from "#utils";

import type { SearchFieldProps } from "#components/SearchField";

/** ExpandableSearchFieldProps interface for the ExpandableSearchField component */
export interface ExpandableSearchFieldProps extends Omit<
  SearchFieldProps,
  "className" | "label" | "assistiveText"
> {
  /** Class name applied to the outer wrapper */
  className?: string;
  /** Width of the field once expanded. Defaults to "12.5rem" */
  expandedWidth?: string;
  /** Accessible label for the collapsed trigger button and the search field. Defaults to "Search" */
  buttonLabel?: string;
  /** Render initially in the expanded state */
  defaultExpanded?: boolean;
  /** Collapse back to the icon button when the field is blurred while empty. Defaults to true */
  collapseWhenEmpty?: boolean;
}

/**
 * A search widget that presents as a circular icon button and expands into a
 * full v2 SearchField when clicked. Collapses back to the button when blurred
 * (or on Escape) while empty.
 */
export function ExpandableSearchField({
  className,
  expandedWidth = "12.5rem",
  buttonLabel = "Search",
  defaultExpanded = false,
  collapseWhenEmpty = true,
  value,
  defaultValue,
  onChange,
  "aria-label": ariaLabel,
  ...props
}: ExpandableSearchFieldProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? "");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const currentValue = value ?? internalValue;

  // Focus the input as soon as the field expands.
  useLayoutEffect(() => {
    if (isExpanded) {
      wrapperRef.current?.querySelector("input")?.focus();
    }
  }, [isExpanded]);

  const handleChange = (next: string) => {
    setInternalValue(next);
    onChange?.(next);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // Collapse only when focus leaves the whole widget and there's nothing to keep.
    if (
      collapseWhenEmpty &&
      currentValue === "" &&
      !event.currentTarget.contains(event.relatedTarget)
    ) {
      setIsExpanded(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && currentValue === "") {
      setIsExpanded(false);
    }
  };

  return (
    <div
      className={cn("relative inline-flex transition-[width] duration-200 ease-in-out", className)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      ref={wrapperRef}
      style={{ width: isExpanded ? expandedWidth : "2rem" }}
    >
      <div
        aria-hidden={!isExpanded || undefined}
        className={cn(
          "w-full transition-opacity duration-200",
          isExpanded ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        inert={!isExpanded}
      >
        <SearchField
          {...props}
          aria-label={ariaLabel ?? buttonLabel}
          className="w-full"
          onChange={handleChange}
          value={currentValue}
        />
      </div>
      <Button
        aria-hidden={isExpanded}
        aria-label={buttonLabel}
        className={cn(
          "absolute inset-0 flex items-center w-10 justify-center rounded-full border border-outline-input-enabled bg-background-primary text-text-secondary transition-all duration-200",
          "hover:border-outline-input-hover hover:text-text-primary hover:ring-1 hover:ring-outline-input-hover p-0",
          "data-[focus-visible]:border-outline-input-focus data-[focus-visible]:text-text-primary data-[focus-visible]:ring-1 data-[focus-visible]:ring-outline-input-focus",
          isExpanded ? "pointer-events-none opacity-0" : "opacity-100",
        )}
        excludeFromTabOrder={isExpanded}
        onPress={() => setIsExpanded(true)}
        variant="plain"
      >
        <SearchIcon size={16} />
      </Button>
    </div>
  );
}
